(() => {
    "use strict";

    const form = document.getElementById("homepageAuditForm");
    const input = document.getElementById("homepageAuditUrl");
    const status = document.getElementById("homepageAuditUrlStatus");
    if (!form || !input || !status) return;
    const initialStatusText = status.textContent.trim();

    const statusClasses = {
        idle: "text-slate-500",
        checking: "text-sky-700",
        success: "text-emerald-700",
        warning: "text-amber-700",
        error: "text-red-700"
    };

    let debounceTimer = null;
    let activeRequest = null;
    let requestNumber = 0;
    let lastCheckedUrl = "";
    let lastCheckResult = null;
    let submitInProgress = false;
    let hostErrorModalBound = false;

    function showStatus(kind = "idle", message = "") {
        status.textContent = "";
        status.title = message;

        if (!message) {
            status.className = "sr-home-url-status mt-2 flex items-start gap-2 text-xs font-medium text-slate-500";
            status.textContent = initialStatusText;
            return;
        }

        status.className = `sr-home-url-status mt-2 flex items-start gap-2 text-xs font-medium ${statusClasses[kind] || statusClasses.idle}`;

        const marker = document.createElement("span");
        marker.className = kind === "checking" ? "animate-pulse" : "";
        marker.setAttribute("aria-hidden", "true");
        marker.textContent = "●";

        const text = document.createElement("span");
        text.className = "min-w-0 break-words";
        text.textContent = message;

        status.append(marker, text);
    }

    function normalizeUrl(value) {
        const raw = String(value || "").trim();
        if (!raw) return "";
        const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        const parsed = new URL(withScheme);
        if (!parsed.hostname || /\s/.test(parsed.hostname)) throw new Error("invalid_url");
        return parsed.toString();
    }

    function ensureHostErrorModal() {
        let modal = document.getElementById("homepageHostErrorModal");
        if (!modal) {
            document.body.insertAdjacentHTML("beforeend", `
                <div id="homepageHostErrorModal" class="fixed inset-0 hidden" style="z-index: 100" role="alertdialog" aria-modal="true" aria-hidden="true" aria-labelledby="homepageHostErrorTitle" aria-describedby="homepageHostErrorText">
                    <div class="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" data-close-host-error="backdrop"></div>
                    <div class="relative flex min-h-full items-center justify-center p-4">
                        <div class="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.22)] sm:p-6">
                            <div class="flex items-start gap-3">
                                <span class="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-lg font-extrabold text-red-700" aria-hidden="true">!</span>
                                <div class="min-w-0">
                                    <h2 id="homepageHostErrorTitle" class="text-lg font-extrabold text-slate-900">Domain nicht gefunden</h2>
                                    <p id="homepageHostErrorText" class="mt-2 text-sm leading-6 text-slate-600">Die eingegebene Domain konnte nicht aufgelöst werden. Bitte prüfen Sie die Schreibweise und versuchen Sie es erneut.</p>
                                    <div id="homepageHostErrorValue" class="mt-3 break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700"></div>
                                </div>
                            </div>
                            <button type="button" id="homepageHostErrorConfirm" class="mt-5 w-full rounded-2xl bg-slate-900 px-5 py-3 text-sm font-extrabold text-white hover:bg-slate-800">OK, Eingabe prüfen</button>
                        </div>
                    </div>
                </div>
            `);
            modal = document.getElementById("homepageHostErrorModal");
        }

        if (modal && !hostErrorModalBound) {
            const closeModal = () => {
                modal.classList.add("hidden");
                modal.setAttribute("aria-hidden", "true");
                document.documentElement.classList.remove("overflow-hidden");
                input.readOnly = false;
                input.focus();
                input.select();
            };

            modal.querySelector("#homepageHostErrorConfirm")?.addEventListener("click", closeModal);
            modal.querySelector("[data-close-host-error='backdrop']")?.addEventListener("click", closeModal);
            modal.addEventListener("keydown", (event) => {
                if (event.key === "Escape") {
                    event.preventDefault();
                    closeModal();
                } else if (event.key === "Tab") {
                    event.preventDefault();
                    modal.querySelector("#homepageHostErrorConfirm")?.focus();
                }
            });
            hostErrorModalBound = true;
        }

        return modal;
    }

    function openHostErrorModal() {
        const modal = ensureHostErrorModal();
        if (!modal) return;
        const value = modal.querySelector("#homepageHostErrorValue");
        if (value) value.textContent = String(input.value || "").trim();
        modal.classList.remove("hidden");
        modal.setAttribute("aria-hidden", "false");
        document.documentElement.classList.add("overflow-hidden");
        window.setTimeout(() => modal.querySelector("#homepageHostErrorConfirm")?.focus(), 0);
    }

    async function checkUrl(options = {}) {
        const showBlockingModal = !!options.showBlockingModal;
        const raw = String(input.value || "").trim();
        if (!raw) {
            lastCheckedUrl = "";
            lastCheckResult = null;
            showStatus();
            return { blocking: true, reason: "empty" };
        }

        let normalizedUrl = "";
        try {
            normalizedUrl = normalizeUrl(raw);
        } catch {
            lastCheckedUrl = "";
            lastCheckResult = { blocking: true, reason: "invalid_url" };
            showStatus("error", "Bitte eine gültige Domain oder URL eingeben.");
            return lastCheckResult;
        }

        if (normalizedUrl === lastCheckedUrl && lastCheckResult) {
            if (showBlockingModal && lastCheckResult.reason === "host_unresolved") openHostErrorModal();
            return lastCheckResult;
        }

        activeRequest?.abort();
        activeRequest = new AbortController();
        const currentRequest = ++requestNumber;
        showStatus("checking", "Erreichbarkeit wird geprüft …");

        try {
            const response = await fetch("/api/seo-audit/preflight-url.php", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: normalizedUrl }),
                signal: activeRequest.signal
            });
            const data = await response.json().catch(() => ({}));
            if (currentRequest !== requestNumber) return;

            if (!response.ok || !data?.ok) {
                const requestError = new Error(data?.error || "Vorprüfung nicht möglich.");
                requestError.code = String(data?.error_code || "");
                requestError.httpStatus = response.status;
                throw requestError;
            }

            lastCheckedUrl = normalizedUrl;
            const level = ["success", "warning", "error"].includes(data.level) ? data.level : "idle";
            showStatus(level, data.message || "Vorprüfung abgeschlossen.");
            lastCheckResult = { blocking: false, reason: "checked", data };
            return lastCheckResult;
        } catch (error) {
            if (error?.name === "AbortError" || currentRequest !== requestNumber) {
                return { blocking: false, reason: "aborted" };
            }

            const blocking = error?.code === "host_unresolved" || [400, 422].includes(Number(error?.httpStatus || 0));
            const message = error?.message || "Vorprüfung nicht möglich – Analyse kann trotzdem gestartet werden.";
            lastCheckedUrl = blocking ? normalizedUrl : "";
            lastCheckResult = { blocking, reason: blocking ? (error?.code || "invalid_target") : "check_unavailable" };
            showStatus(blocking ? "error" : "warning", message);
            if (showBlockingModal && error?.code === "host_unresolved") openHostErrorModal();
            return lastCheckResult;
        }
    }

    function scheduleCheck() {
        clearTimeout(debounceTimer);
        activeRequest?.abort();
        requestNumber += 1;
        lastCheckedUrl = "";
        lastCheckResult = null;

        const raw = String(input.value || "").trim();
        if (raw.length < 4) {
            showStatus();
            return;
        }

        showStatus("idle", "Eingabe wird gleich geprüft …");
        debounceTimer = window.setTimeout(checkUrl, 700);
    }

    input.addEventListener("input", scheduleCheck);
    input.addEventListener("blur", () => {
        clearTimeout(debounceTimer);
        checkUrl();
    });

    form.addEventListener("submit", async (event) => {
        if (submitInProgress) return;
        event.preventDefault();
        clearTimeout(debounceTimer);
        activeRequest?.abort();

        if (!String(input.value || "").trim()) {
            showStatus("error", "Bitte zuerst eine Domain oder URL eingeben.");
            input.focus();
            return;
        }

        const submitButton = form.querySelector("[type='submit']");
        const submitButtonLabel = submitButton?.textContent?.trim() || "SEO-Check starten";
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = "Audit wird gestartet...";
        }
        input.readOnly = true;

        const result = await checkUrl({ showBlockingModal: true });
        if (result?.blocking) {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = submitButton.dataset.idleLabel || submitButtonLabel;
            }
            input.readOnly = false;
            if (result.reason !== "host_unresolved") {
                input.focus();
                input.select();
            }
            return;
        }

        submitInProgress = true;
        showStatus("checking", "Analyse wird vorbereitet …");
        HTMLFormElement.prototype.submit.call(form);
    });

    const pageParams = new URLSearchParams(window.location.search);
    if (pageParams.get("error") === "host-unresolved") {
        const previousUrl = String(pageParams.get("uri") || "").trim();
        if (previousUrl) input.value = previousUrl;
        try {
            lastCheckedUrl = previousUrl ? normalizeUrl(previousUrl) : "";
        } catch {
            lastCheckedUrl = "";
        }
        lastCheckResult = { blocking: true, reason: "host_unresolved" };
        showStatus("error", "Domain konnte nicht gefunden werden. Bitte prüfen Sie die Schreibweise.");
        window.history.replaceState(window.history.state, "", "/#check");
        window.setTimeout(openHostErrorModal, 0);
    }
})();
